// src/shared/utils/berlinTime.ts
//
// Zentrale Utility für Europe/Berlin-Zeitlogik bei terminiertem Versand.
// Die Logik ist identisch zu den bewährten privaten Helfern im Ticketsystem
// (TicketService.berlinLocalToUtcIso/berlinOffsetMinutes bzw. der
// berlinNowLocalString-Helper der Composer). Der Ticket-Bestand bleibt bewusst
// unangetastet (kein Refactoring an produktivem Code); neue Features
// (geschützter Downloadbereich, später Finalisieren) nutzen diese Utility.
// Eine spätere Konsolidierung des Ticketsystems auf diese Datei ist möglich,
// aber eine separate Entscheidung.

/**
 * Interpretiert eine Wanduhrzeit ("YYYY-MM-DDTHH:mm", z. B. aus einem
 * datetime-local-Feld) fest als Europe/Berlin und gibt den entsprechenden
 * UTC-Zeitpunkt als ISO-String zurück. DST-korrekt über Intl. Die seltene
 * Doppel-/Lücken-Stunde an der Zeitumstellung wird nicht gesondert behandelt
 * (akzeptabler Randfall für Terminierung).
 *
 * Wirft einen Error bei ungültigem Format.
 */
export function berlinLocalToUtcIso(local: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!match) {
    throw new Error('Ungültiges Datums-/Zeitformat');
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);

  const utcGuess = Date.UTC(year, month - 1, day, hour, minute);
  const offsetMinutes = berlinOffsetMinutes(new Date(utcGuess));
  return new Date(utcGuess - offsetMinutes * 60000).toISOString();
}

/**
 * Aktuelle Berliner Wanduhrzeit als "YYYY-MM-DDTHH:mm" für das min-Attribut
 * von datetime-local-Feldern (UI-seitige Schranke gegen offensichtlich
 * vergangene Zeitpunkte; die verbindliche Prüfung passiert im Service bzw.
 * serverseitig).
 */
export function berlinNowLocalString(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(new Date());
  const m: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') m[p.type] = p.value;
  }
  return `${m.year}-${m.month}-${m.day}T${m.hour}:${m.minute}`;
}

/**
 * Berliner Wanduhrzeit des Folgetags zur angegebenen Uhrzeit als
 * "YYYY-MM-DDTHH:mm" (Vorbelegung von datetime-local-Feldern beim
 * terminierten Versand). Maßgeblich ist das heutige Kalenderdatum in
 * Europe/Berlin, nicht die Zeitzone des Browsers. Monats- und Jahreswechsel
 * werden über Date.UTC korrekt fortgeschrieben.
 */
export function berlinTomorrowLocalString(hour: number, minute: number): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date());
  const m: Record<string, number> = {};
  for (const p of parts) {
    if (p.type !== 'literal') m[p.type] = Number(p.value);
  }
  const folgetag = new Date(Date.UTC(m.year, m.month - 1, m.day + 1));
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${folgetag.getUTCFullYear()}-${pad(folgetag.getUTCMonth() + 1)}-${pad(folgetag.getUTCDate())}T${pad(hour)}:${pad(minute)}`;
}

/**
 * Formatiert einen UTC-Zeitstempel fest in Europe/Berlin als
 * "DD.MM.YYYY, HH:mm" (für die Anzeige geplanter Versandzeitpunkte).
 * Liefert "—" bei fehlendem oder ungültigem Zeitstempel.
 */
export function formatBerlinDateTime(isoTimestamp: string | null): string {
  if (!isoTimestamp) return '—';
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return '—';
  const parts = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(date);
  const m: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') m[p.type] = p.value;
  }
  return `${m.day}.${m.month}.${m.year}, ${m.hour}:${m.minute}`;
}

/** Offset von Europe/Berlin gegenüber UTC (in Minuten) zum gegebenen Zeitpunkt. */
function berlinOffsetMinutes(date: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const map: Record<string, number> = {};
  for (const part of dtf.formatToParts(date)) {
    if (part.type !== 'literal') map[part.type] = Number(part.value);
  }
  const asUtc = Date.UTC(map.year, map.month - 1, map.day, map.hour, map.minute, map.second);
  return Math.round((asUtc - date.getTime()) / 60000);
}
